import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Image, Input, Modal, Spin, Upload, message } from "antd";
import { LikeFilled, LikeOutlined, PlusOutlined, StarFilled, StarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { EasyAvatar } from "fin-web-components";
import { observer } from "mobx-react-lite";
import { uploadAppFileToOss } from "../../global/OssUploadApi";
import { avatarDisplayWord } from "../../utils/avatarDisplayWord";
import { QaFeedApi } from "./QaFeedApi";
import type { QaFeedQuestion, QaFeedTab, QaQuotaResult, QaThreadItem } from "./QaFeedType";
import "./QaFeedPage.css";

const PAGE_SIZE = 8;
const MAX_IMAGES = 3;
const CARD_THREAD_PREVIEW_LIMIT = 2;

function threadPreviewForCard(items: QaThreadItem[]): { visible: QaThreadItem[]; hiddenCount: number } {
  const list = items ?? [];
  const hiddenCount = Math.max(0, list.length - CARD_THREAD_PREVIEW_LIMIT);
  const visible = list.slice(-CARD_THREAD_PREVIEW_LIMIT).reverse();
  return { visible, hiddenCount };
}

function threadItemsNewestFirst(items: QaThreadItem[]): QaThreadItem[] {
  return [...items].reverse();
}

function displayTime(v: string): string {
  const d = dayjs(v);
  return d.isValid() ? d.format("M月D日 HH:mm") : v;
}

function QuestionPhotos({ urls }: { urls: string[] }) {
  const list = urls.slice(0, MAX_IMAGES);
  if (list.length === 0) return null;
  if (list.length === 1) {
    return (
      <Image.PreviewGroup>
        <div className="lf-qa-photos-single">
          <Image src={list[0]} alt="" />
        </div>
      </Image.PreviewGroup>
    );
  }
  return (
    <Image.PreviewGroup>
      <div className="lf-qa-photos-thumbs">
        {list.map((src, i) => (
          <div key={`${src}-${i}`} className="lf-qa-photos-thumb">
            <Image src={src} alt="" preview={{ mask: false }} />
          </div>
        ))}
      </div>
    </Image.PreviewGroup>
  );
}

function latestStaffAnswerItem(items: QaThreadItem[]): QaThreadItem | undefined {
  for (let i = items.length - 1; i >= 0; i -= 1) {
    if (items[i].kind === "staff_answer") return items[i];
  }
  return undefined;
}

function DetailReplyCompose({
  draft,
  onDraftChange,
  onSubmit,
  submitting,
  remainReplies,
}: {
  draft: string;
  onDraftChange: (v: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  remainReplies: number | null;
}) {
  return (
    <div className="lf-qa-detail-reply-compose">
      {typeof remainReplies === "number" ? (
        <div className="lf-qa-detail-reply-quota">今日还可回复 {remainReplies} 次</div>
      ) : null}
      <div className="lf-qa-detail-reply-input-wrap">
        <Input.TextArea
          className="lf-qa-detail-reply-textarea-wrap"
          classNames={{ textarea: "lf-qa-detail-reply-textarea" }}
          variant="borderless"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          placeholder="输入追问内容"
          rows={1}
        />
        <button
          type="button"
          className="lf-qa-text-btn lf-qa-detail-reply-send"
          disabled={submitting || remainReplies === 0}
          onClick={onSubmit}
        >
          发送
        </button>
      </div>
    </div>
  );
}

function ThreadBlock({
  item,
  showReplyBtn,
  replyOpen,
  replyDraft,
  remainReplies,
  submittingReply,
  onOpenReply,
  onCancelReply,
  onReplyDraftChange,
  onSubmitReply,
}: {
  item: QaThreadItem;
  showReplyBtn?: boolean;
  replyOpen?: boolean;
  replyDraft?: string;
  remainReplies?: number | null;
  submittingReply?: boolean;
  onOpenReply?: () => void;
  onCancelReply?: () => void;
  onReplyDraftChange?: (v: string) => void;
  onSubmitReply?: () => void;
}) {
  const isStaff = item.kind === "staff_answer";
  const name = item.authorNickname?.trim() || (isStaff ? "老师" : "我");
  return (
    <div className="lf-qa-card__reply">
      <EasyAvatar
        shape="circle"
        size={26}
        url={item.authorAvatar?.trim() || undefined}
        word={avatarDisplayWord(name)}
        alt=""
        className="lf-qa-card__reply-avatar"
      />
      <div className="lf-qa-card__reply-main">
        <p className="lf-qa-card__reply-line">
          <span className="lf-qa-card__reply-author">{name}</span>
          <span>：</span>
          {item.text}
          {showReplyBtn && onOpenReply ? (
            <>
              <span className="lf-qa-reply-gap" />
              <button type="button" className="lf-qa-text-btn" onClick={onOpenReply}>
                回复
              </button>
            </>
          ) : null}
        </p>
        {item.at ? <div className="lf-qa-card__reply-meta">{displayTime(item.at)}</div> : null}
        {replyOpen ? (
          <div className="lf-qa-inline-reply">
            {typeof remainReplies === "number" ? (
              <div className="lf-qa-inline-reply-quota">今日还可回复 {remainReplies} 次</div>
            ) : null}
            <Input.TextArea
              value={replyDraft ?? ""}
              onChange={(e) => onReplyDraftChange?.(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 5 }}
              placeholder="输入追问内容"
            />
            <div className="lf-qa-inline-reply-actions">
              <button type="button" className="lf-qa-text-btn" disabled={submittingReply} onClick={onCancelReply}>
                取消
              </button>
              <button
                type="button"
                className="lf-qa-text-btn lf-qa-text-btn--primary"
                disabled={submittingReply || remainReplies === 0}
                onClick={onSubmitReply}
              >
                发送
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function threadItemsOf(row: QaFeedQuestion): QaThreadItem[] {
  if (row.threadItems?.length) return row.threadItems;
  return (row.answers ?? []).map((a) => ({
    id: a.id,
    kind: "staff_answer" as const,
    text: a.answerText,
    at: a.answeredAt,
    authorNickname: a.answererNickname,
    authorAvatar: a.answererAvatar,
  }));
}

function hasStaffAnswer(row: QaFeedQuestion): boolean {
  return threadItemsOf(row).some((x) => x.kind === "staff_answer");
}

export interface QaFeedPageProps {
  spaceId: string;
  spaceProductId: string;
  qaAllowPublic?: boolean;
}

const QaFeedPage: React.FC<QaFeedPageProps> = observer(
  ({ spaceId, spaceProductId, qaAllowPublic = false }) => {
    const showPublicTab = qaAllowPublic;
    const [tab, setTab] = useState<QaFeedTab>(showPublicTab ? "public" : "mine");
    const [items, setItems] = useState<QaFeedQuestion[]>([]);
    const [total, setTotal] = useState(0);
    const [pageNum, setPageNum] = useState(1);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const [askOpen, setAskOpen] = useState(false);
    const [askText, setAskText] = useState("");
    const [askImages, setAskImages] = useState<string[]>([]);
    const [uploadingImg, setUploadingImg] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [quota, setQuota] = useState<QaQuotaResult | null>(null);
    const [replyOpenKey, setReplyOpenKey] = useState<string | null>(null);
    const [replyDraft, setReplyDraft] = useState("");
    const [detailReplyDraft, setDetailReplyDraft] = useState("");
    const [submittingReply, setSubmittingReply] = useState(false);
    const [detailOpen, setDetailOpen] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);

    const sentinelRef = useRef<HTMLDivElement | null>(null);

    const activeRow = useMemo(
      () => (activeId ? items.find((x) => x.id === activeId) : undefined),
      [activeId, items]
    );

    const fetchQuota = useCallback(async () => {
      const res = await QaFeedApi.quota({ spaceId, spaceProductId });
      if (typeof res === "string") {
        message.error(res);
        return null;
      }
      setQuota(res);
      return res;
    }, [spaceId, spaceProductId]);

    useEffect(() => {
      if (askOpen) void fetchQuota();
    }, [askOpen, fetchQuota]);

    useEffect(() => {
      if (!showPublicTab && tab === "public") {
        setTab("mine");
      }
    }, [showPublicTab, tab]);

    const reloadFirstPage = useCallback(async () => {
      setLoading(true);
      try {
        const activeTab = showPublicTab ? tab : "mine";
        const res = await QaFeedApi.listFeed({
          tab: activeTab,
          pageNum: 1,
          pageSize: PAGE_SIZE,
          spaceId,
          spaceProductId,
        });
        if (typeof res === "string") {
          message.error(res);
          setItems([]);
          setTotal(0);
          setPageNum(1);
          return;
        }
        setItems(res.list ?? []);
        setTotal(res.total ?? 0);
        setPageNum(1);
      } finally {
        setLoading(false);
      }
    }, [tab, showPublicTab, spaceId, spaceProductId]);

    useEffect(() => {
      void reloadFirstPage();
    }, [reloadFirstPage]);

    const loadMore = useCallback(async () => {
      if (loading || loadingMore) return;
      if (total === 0 || items.length >= total) return;
      setLoadingMore(true);
      try {
        const activeTab = showPublicTab ? tab : "mine";
        const nextPage = pageNum + 1;
        const res = await QaFeedApi.listFeed({
          tab: activeTab,
          pageNum: nextPage,
          pageSize: PAGE_SIZE,
          spaceId,
          spaceProductId,
        });
        if (typeof res === "string") {
          message.error(res);
          return;
        }
        setItems((prev) => [...prev, ...(res.list ?? [])]);
        setPageNum(nextPage);
      } finally {
        setLoadingMore(false);
      }
    }, [items.length, loading, loadingMore, pageNum, tab, total, showPublicTab, spaceId, spaceProductId]);

    useEffect(() => {
      const el = sentinelRef.current;
      if (!el) return undefined;
      const ob = new IntersectionObserver(
        (entries) => {
          if (entries[0]?.isIntersecting) void loadMore();
        },
        { root: null, rootMargin: "100px", threshold: 0 }
      );
      ob.observe(el);
      return () => ob.disconnect();
    }, [loadMore]);

    const patchItem = useCallback((id: string, patch: Partial<QaFeedQuestion>) => {
      setItems((prev) => prev.map((x) => (x.id === id ? { ...x, ...patch } : x)));
    }, []);

    const toggleLike = async (e: React.MouseEvent, row: QaFeedQuestion) => {
      e.stopPropagation();
      const res = await QaFeedApi.toggleLike({ spaceId, spaceProductId, questionId: row.id });
      if (typeof res === "string") {
        message.error(res);
        return;
      }
      patchItem(row.id, {
        likedByMe: res.liked,
        likeCount: res.likeCount,
        collectCount: res.collectCount,
        collectedByMe: res.collected,
      });
    };

    const toggleCollect = async (e: React.MouseEvent, row: QaFeedQuestion) => {
      e.stopPropagation();
      const res = await QaFeedApi.toggleCollect({ spaceId, spaceProductId, questionId: row.id });
      if (typeof res === "string") {
        message.error(res);
        return;
      }
      patchItem(row.id, {
        likedByMe: res.liked,
        likeCount: res.likeCount,
        collectCount: res.collectCount,
        collectedByMe: res.collected,
      });
    };

    const uploadImage = async (file: File) => {
      if (askImages.length >= MAX_IMAGES) {
        message.warning(`最多上传 ${MAX_IMAGES} 张图片`);
        return;
      }
      setUploadingImg(true);
      try {
        const url = await uploadAppFileToOss(file, "QA_QUESTION_IMAGE");
        if (url) setAskImages((prev) => [...prev, url].slice(0, MAX_IMAGES));
      } catch (err) {
        message.error(err instanceof Error ? err.message : "上传失败");
      } finally {
        setUploadingImg(false);
      }
    };

    const removeAskImage = (src: string) => {
      setAskImages((prev) => prev.filter((x) => x !== src));
    };

    const submitQuestion = async () => {
      const text = askText.trim();
      if (!text) {
        message.warning("请填写问题描述");
        return;
      }
      if (quota && quota.remainQuestionsToday <= 0) {
        message.warning("今日提问次数已达上限");
        return;
      }
      setSubmitting(true);
      try {
        const res = await QaFeedApi.submitQuestion({
          spaceId,
          spaceProductId,
          text,
          imageUrls: askImages,
        });
        if (typeof res === "string") {
          message.error(res);
          return;
        }
        message.success("提问已提交，请等待老师回复");
        setAskOpen(false);
        setAskText("");
        setAskImages([]);
        setTab("mine");
        void reloadFirstPage();
      } finally {
        setSubmitting(false);
      }
    };

    const openCustomerReply = async (questionId: string, answerId: string) => {
      setReplyOpenKey(`${questionId}::${answerId}`);
      setReplyDraft("");
      await fetchQuota();
    };

    const cancelCustomerReply = () => {
      setReplyOpenKey(null);
      setReplyDraft("");
    };

    const openDetail = (row: QaFeedQuestion) => {
      cancelCustomerReply();
      setDetailReplyDraft("");
      setActiveId(row.id);
      setDetailOpen(true);
      if (row.isMine) void fetchQuota();
    };

    const closeDetail = () => {
      setDetailOpen(false);
      setActiveId(null);
      setDetailReplyDraft("");
      cancelCustomerReply();
    };

    const renderThreadBlocks = (
      row: QaFeedQuestion,
      threadItems: QaThreadItem[],
      more?: { hiddenCount: number; onMore: () => void },
      enableReply = true
    ) => {
      if (threadItems.length === 0) return null;
      return (
        <div className="lf-qa-card__replies-box">
          {threadItems.map((item) => {
            const replyKey = `${row.id}::${item.id}`;
            const canReply = enableReply && Boolean(row.isMine && item.kind === "staff_answer");
            return (
              <ThreadBlock
                key={replyKey}
                item={item}
                showReplyBtn={canReply && replyOpenKey !== replyKey}
                replyOpen={replyOpenKey === replyKey}
                replyDraft={replyDraft}
                remainReplies={quota?.remainRepliesToday ?? null}
                submittingReply={submittingReply}
                onOpenReply={() => void openCustomerReply(row.id, item.id)}
                onCancelReply={cancelCustomerReply}
                onReplyDraftChange={setReplyDraft}
                onSubmitReply={() => void submitCustomerReply(row, item.id)}
              />
            );
          })}
          {more && more.hiddenCount > 0 ? (
            <button
              type="button"
              className="lf-qa-text-btn lf-qa-thread-more"
              onClick={(e) => {
                e.stopPropagation();
                more.onMore();
              }}
            >
              查看更多{more.hiddenCount}条消息
            </button>
          ) : null}
        </div>
      );
    };

    const submitCustomerReply = async (row: QaFeedQuestion, answerId: string, textOverride?: string) => {
      const text = (textOverride ?? replyDraft).trim();
      if (!text) {
        message.warning("请填写回复内容");
        return;
      }
      if (quota && quota.remainRepliesToday <= 0) {
        message.warning("今日回复次数已达上限");
        return;
      }
      setSubmittingReply(true);
      try {
        const res = await QaFeedApi.submitReply({
          spaceId,
          spaceProductId,
          questionId: row.id,
          answerId,
          replyText: text,
        });
        if (typeof res === "string") {
          message.error(res);
          return;
        }
        message.success("回复已发送");
        patchItem(row.id, res);
        cancelCustomerReply();
        setDetailReplyDraft("");
        void fetchQuota();
      } finally {
        setSubmittingReply(false);
      }
    };

    const submitDetailReply = async () => {
      if (!activeRow) return;
      const staffItem = latestStaffAnswerItem(threadItemsOf(activeRow));
      if (!staffItem) {
        message.warning("暂无老师回复，无法追问");
        return;
      }
      await submitCustomerReply(activeRow, staffItem.id, detailReplyDraft);
    };

    const activeTab = showPublicTab ? tab : "mine";
    const showEnd = !loading && total > 0 && items.length >= total;

    return (
      <div className="lf-qa-feed">
        <div className="lf-qa-feed__inner">
          <div className="lf-qa-feed__toolbar">
            <div className="lf-qa-feed__filters">
              {showPublicTab ? (
                <button
                  type="button"
                  className={`lf-qa-feed__filter${activeTab === "public" ? " lf-qa-feed__filter--on" : ""}`}
                  onClick={() => setTab("public")}
                >
                  会员可见
                </button>
              ) : null}
              <button
                type="button"
                className={`lf-qa-feed__filter${activeTab === "mine" ? " lf-qa-feed__filter--on" : ""}`}
                onClick={() => setTab("mine")}
              >
                我的提问
              </button>
            </div>
            <Button
              type="primary"
              className="lf-qa-feed__ask-btn"
              style={{ borderRadius: 5 }}
              onClick={() => setAskOpen(true)}
            >
              我要提问
            </Button>
          </div>

          <Spin spinning={loading}>
            {items.length === 0 && !loading ? (
              <div className="lf-qa-empty">
                {activeTab === "public"
                  ? "暂无公开问答，运营审核公开后将展示在此"
                  : "您还没有提问，点击右上角提交问题"}
              </div>
            ) : (
              <div className="lf-qa-feed__list">
                {items.map((row) => (
                  <article key={row.id} className="lf-qa-card">
                    <div className="lf-qa-card__head">
                      <EasyAvatar
                        shape="square"
                        squareRadius={4}
                        size={30}
                        style={{marginTop:3}}
                        url={row.askerAvatar?.trim() || undefined}
                        word={avatarDisplayWord(row.askerNickname, row.askerDisplayId)}
                        alt=""
                      />
                      <div className="lf-qa-card__main">
                        <div className="lf-qa-card__user-line">
                          <div className="lf-qa-card__user-info">
                            <span className="lf-qa-card__asker">
                              {row.askerNickname}
                              {row.isMine ? <span className="lf-qa-card__asker-me">（我）</span> : null}
                            </span>
                            <span className="lf-qa-card__time">{displayTime(row.askedAt)}</span>
                          </div>
                          {qaAllowPublic ? (
                            <div className="lf-qa-card__action-btns">
                              <button
                                type="button"
                                className="lf-qa-action-btn"
                                onClick={(e) => void toggleLike(e, row)}
                              >
                                {row.likedByMe ? (
                                  <LikeFilled className="lf-qa-action-ic lf-qa-action-ic--on" />
                                ) : (
                                  <LikeOutlined className="lf-qa-action-ic" />
                                )}
                                <span>{row.likeCount ?? 0}</span>
                              </button>
                              <button
                                type="button"
                                className="lf-qa-action-btn"
                                onClick={(e) => void toggleCollect(e, row)}
                              >
                                {row.collectedByMe ? (
                                  <StarFilled className="lf-qa-action-ic lf-qa-action-ic--collect-on" />
                                ) : (
                                  <StarOutlined className="lf-qa-action-ic" />
                                )}
                                <span>{row.collectCount ?? 0}</span>
                              </button>
                            </div>
                          ) : null}
                        </div>
                        <div className="lf-qa-card__text">{row.text}</div>
                        {row.images?.length ? (
                          <div className="lf-qa-card__photos">
                            <QuestionPhotos urls={row.images} />
                          </div>
                        ) : null}
                        {(() => {
                          const threadItems = threadItemsOf(row);
                          const { visible, hiddenCount } = threadPreviewForCard(threadItems);
                          return renderThreadBlocks(
                            row,
                            visible,
                            hiddenCount > 0 ? { hiddenCount, onMore: () => openDetail(row) } : undefined
                          );
                        })()}
                        {!hasStaffAnswer(row) && row.isMine ? (
                          <div className="lf-qa-card__badge">等待老师回复</div>
                        ) : null}
                        {showPublicTab && row.isPublic ? (
                          <div className="lf-qa-card__meta">
                            <span className="lf-qa-card__badge">所有会员可见</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </article>
                ))}
                <div ref={sentinelRef} className="lf-qa-sentinel" />
                {loadingMore ? (
                  <div className="lf-qa-loading-more">
                    <Spin size="small" /> 加载中…
                  </div>
                ) : null}
                {showEnd ? <div className="lf-qa-end-hint">已经到底啦</div> : null}
              </div>
            )}
          </Spin>
        </div>

        <Modal
          className="lf-qa-modal"
          title="我要提问"
          open={askOpen}
          onCancel={() => {
            if (submitting) return;
            setAskOpen(false);
          }}
          destroyOnHidden
          footer={
            <div className="lf-qa-modal-footer">
              {qaAllowPublic ? (
                <span className="lf-qa-modal-hint">
                  您的提问将有概率被推荐到所有会员可见。
                </span>
              ) : (
                <span className="lf-qa-modal-hint lf-qa-modal-hint--empty" />
              )}
              <div className="lf-qa-modal-footer-btns">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => setAskOpen(false)}
                  style={{
                    width: 80,
                    height: 32,
                    margin: 0,
                    padding: 0,
                    boxSizing: "border-box",
                    borderRadius: 4,
                    border: "1px solid #d9d9d9",
                    background: "#fff",
                    color: "rgba(0, 0, 0, 0.88)",
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: "30px",
                    cursor: submitting ? "not-allowed" : "pointer",
                    flexShrink: 0,
                  }}
                >
                  取消
                </button>
                <button
                  type="button"
                  disabled={submitting || (quota != null && quota.remainQuestionsToday <= 0)}
                  onClick={() => void submitQuestion()}
                  style={{
                    width: 80,
                    height: 32,
                    margin: 0,
                    padding: 0,
                    boxSizing: "border-box",
                    borderRadius: 4,
                    border: "1px solid #ec407a",
                    background: "#ec407a",
                    color: "#fff",
                    fontSize: 14,
                    fontWeight: 400,
                    lineHeight: "30px",
                    cursor: submitting ? "not-allowed" : "pointer",
                    opacity: submitting ? 0.65 : 1,
                    flexShrink: 0,
                  }}
                >
                  提交
                </button>
              </div>
            </div>
          }
        >
          {quota ? (
            <div className="lf-qa-modal-quota">今日最多还可提问 {quota.remainQuestionsToday} 次</div>
          ) : null}
          <Input.TextArea
            className="lf-qa-modal-textarea"
            value={askText}
            onChange={(e) => setAskText(e.target.value)}
            placeholder="请描述您的问题，可补充背景与诉求"
            autoSize={{ minRows: 4, maxRows: 10 }}
            bordered={false}
          />
          <div className="lf-qa-modal-uploads">
            {askImages.map((src) => (
              <div key={src} className="lf-qa-modal-upload-item">
                <img src={src} alt="" className="lf-qa-modal-upload-thumb" />
                <button
                  type="button"
                  className="lf-qa-modal-upload-remove"
                  onClick={() => removeAskImage(src)}
                  aria-label="移除图片"
                >
                  ×
                </button>
              </div>
            ))}
            {askImages.length < MAX_IMAGES ? (
              <Upload
                showUploadList={false}
                accept="image/*"
                disabled={uploadingImg}
                beforeUpload={(file) => {
                  void uploadImage(file);
                  return false;
                }}
              >
                <button type="button" className="lf-qa-modal-upload-add" disabled={uploadingImg}>
                  {uploadingImg ? <Spin size="small" /> : <PlusOutlined />}
                </button>
              </Upload>
            ) : null}
          </div>
        </Modal>

        <Modal
          className="lf-qa-modal lf-qa-detail-modal"
          rootClassName="lf-qa-question-modal"
          title="问题详情"
          open={detailOpen}
          onCancel={closeDetail}
          width={680}
          centered
          destroyOnHidden
          footer={null}
          styles={{
            content: {
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              padding: 0,
            },
            header: {
              flexShrink: 0,
              padding: "16px 24px",
              margin: 0,
            },
            body: {
              flex: "1 1 auto",
              minHeight: 0,
              overflow: "hidden",
              padding: 0,
              display: "flex",
              flexDirection: "column",
            },
          }}
        >
          {activeRow ? (
            <>
              <div className="lf-qa-detail-q">
                <div className="lf-qa-card__head">
                  <EasyAvatar
                    shape="square"
                    squareRadius={4}
                    size={30}
                    style={{ marginTop: 3 }}
                    url={activeRow.askerAvatar?.trim() || undefined}
                    word={avatarDisplayWord(activeRow.askerNickname, activeRow.askerDisplayId)}
                    alt=""
                  />
                  <div className="lf-qa-card__main">
                    <div className="lf-qa-card__user-line">
                      <div className="lf-qa-card__user-info">
                        <span className="lf-qa-card__asker">
                          {activeRow.askerNickname}
                          {activeRow.isMine ? <span className="lf-qa-card__asker-me">（我）</span> : null}
                        </span>
                        <span className="lf-qa-card__time">{displayTime(activeRow.askedAt)}</span>
                      </div>
                    </div>
                    <div className="lf-qa-card__text">{activeRow.text}</div>
                    {activeRow.images?.length ? (
                      <div className="lf-qa-card__photos">
                        <QuestionPhotos urls={activeRow.images} />
                      </div>
                    ) : null}
                  </div>
                </div>
                {activeRow.isMine && latestStaffAnswerItem(threadItemsOf(activeRow)) ? (
                  <DetailReplyCompose
                    draft={detailReplyDraft}
                    onDraftChange={setDetailReplyDraft}
                    onSubmit={() => void submitDetailReply()}
                    submitting={submittingReply}
                    remainReplies={quota?.remainRepliesToday ?? null}
                  />
                ) : null}
              </div>
              <div className="lf-qa-detail-thread-scroll">
                {threadItemsOf(activeRow).length > 0 ? (
                  renderThreadBlocks(activeRow, threadItemsNewestFirst(threadItemsOf(activeRow)), undefined, false)
                ) : (
                  <div className="lf-qa-detail-thread-empty">暂无回复</div>
                )}
              </div>
            </>
          ) : null}
        </Modal>
      </div>
    );
  }
);

export default QaFeedPage;
